// src/features/streamer-widget/ui/StreamerWidgetSettings.jsx
import React, { useState, useEffect } from 'react';
import { ExternalLink, Copy, Eye, EyeOff, Save, Palette, Layout, Link as LinkIcon, Play, Type } from 'lucide-react';
import api from '@shared/api/api';
import { useAuth, useTooltip } from '@app/providers';
import './StreamerWidgetSettings.css';

const THEME_PRESETS = {
  classic: {
    name: 'CLASSIC (RETRO)',
    colors: { background: 'rgba(10, 26, 26, 0.9)', text: '#ffdd88', accent: '#ffaa44', success: '#6aff6a' },
    font: { family: "'Press Start 2P', monospace", size_title: 16, size_main: 14, size_meta: 10, size_score: 18, size_timer: 32 }
  },
  minimal: {
    name: 'MINIMAL',
    colors: { background: 'rgba(0, 0, 0, 0.5)', text: '#ffffff', accent: '#6aff6a', success: '#28a745' },
    font: { family: 'Inter, sans-serif', size_title: 18, size_main: 16, size_meta: 12, size_score: 20, size_timer: 36 }
  },
  neon: {
    name: 'NEON',
    colors: { background: 'rgba(20, 0, 40, 0.8)', text: '#00ffff', accent: '#ff00ff', success: '#00ff00' },
    font: { family: 'Orbitron, sans-serif', size_title: 20, size_main: 18, size_meta: 14, size_score: 24, size_timer: 40 }
  },
  custom: {
    name: 'CUSTOM',
    colors: { background: 'rgba(0, 0, 0, 0.7)', text: '#ffffff', accent: '#ffaa44', success: '#6aff6a' },
    font: { family: "'Press Start 2P', monospace", size_title: 16, size_main: 14, size_meta: 10, size_score: 18, size_timer: 32 }
  }
};

/**
 * Единый компонент верстки виджета для синхронизации Preview и Реальной страницы.
 */
export const WidgetUI = ({ settings, user, challenge, session, localSeconds, formatTime }) => {
  const fontSettings = settings.font || THEME_PRESETS.classic.font;
  
  return (
    <div className="widget-layout-vertical">
      <div className="wv-hero">
        <img src="/images/widget_logo.png" alt="MyUGC" className="wv-logo" style={{ height: '96px', width: '96px' }} />
        <div className="wv-user-info">
          <div className="wv-username" style={{ fontSize: `${fontSettings.size_title}px` }}>
            {user?.display_name || user?.username || 'USER_NAME'}
          </div>
          <div className="wv-rating" style={{ color: settings.colors.accent }}>
            [{user?.rating || 1250} PTS]
          </div>
        </div>
      </div>

      <div className="wv-divider" style={{ backgroundColor: settings.colors.accent }} />

      {/* META: Template & Difficulty */}
      <div className="wv-challenge-meta" style={{ fontSize: `${fontSettings.size_meta}px` }}>
        <div className="wv-meta-line">
          <span className="wv-label">CHALLENGE:</span>
          <span className="wv-value">{challenge?.title || 'EXTRA LIFE'}</span>
        </div>
        <div className="wv-meta-line">
          <span className="wv-label">MODE:</span>
          <span className="wv-value" style={{ color: challenge?.difficulty?.color || settings.colors.accent }}>
            {(challenge?.difficulty?.name || 'NORMAL').toUpperCase()}
          </span>
        </div>
      </div>

      {/* SCORE BLOCK: КРУПНО И ОТДЕЛЬНО */}
      {settings.display.show_score && (
        <div className="wv-score-block">
          <div className="wv-score-label">CURRENT SCORE:</div>
          <div className="wv-score-value" style={{ fontSize: `${fontSettings.size_score}px`, color: settings.colors.accent }}>
            {challenge?.progress?.score || 1250}
          </div>
        </div>
      )}

      {/* GAME BLOCK */}
      <div className="wv-game-box">
        <div className="wv-element-name" style={{ color: settings.colors.accent }}>
          {session?.element_name || 'ТЕКУЩАЯ ИГРА'}
        </div>
        <div className="wv-game-title" style={{ fontSize: `${fontSettings.size_main}px` }}>
          {session?.game_title || 'SUPER MARIO BROS.'}
        </div>
        <div className="wv-game-footer">
          {settings.display.show_platform && (session?.platform || 'NES') && (
            <span className="wv-platform" style={{ backgroundColor: settings.colors.accent }}>
              {session?.platform || 'NES'}
            </span>
          )}
        </div>
      </div>

      {/* TIMER */}
      {settings.display.show_timer && (
        <div className="wv-timer-box">
          <div className="wv-timer" style={{ color: settings.colors.accent, fontSize: `${fontSettings.size_timer}px` }}>
            {formatTime ? formatTime(localSeconds) : "00:15:42"}
          </div>
        </div>
      )}
    </div>
  );
};

const StreamerWidgetSettings = ({ challengeId }) => {
  const { api, user: currentUser } = useAuth();
  const [widget, setWidget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showUrl, setShowUrl] = useState(false);

  useEffect(() => {
    if (challengeId) fetchWidgetSettings();
  }, [challengeId]);

  const fetchWidgetSettings = async () => {
    try {
      setLoading(true);
      const params = { challenge_id: challengeId };
      const response = await api.get('/website/widgets/manage/', { params });
      const data = response.data;
      if (!data.settings?.theme) data.settings = { ...THEME_PRESETS.classic, frame_type: 'auto', theme: 'classic' };
      if (!data.settings.font) data.settings.font = { ...THEME_PRESETS.classic.font };
      setWidget(data);
    } catch (err) {
      setWidget({ is_active: true, settings: { ...THEME_PRESETS.classic, frame_type: 'auto' }, public_url: '' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.patch('/website/widgets/manage/', {
        is_active: widget.is_active,
        settings: widget.settings
      }, { params: { challenge_id: challengeId } });
      setWidget(prev => ({ ...prev, ...response.data }));
      setMessage({ type: 'success', text: 'ПАРАМЕТРЫ ЗАФИКСИРОВАНЫ' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (err) {
      setMessage({ type: 'error', text: 'ОШИБКА СОХРАНЕНИЯ' });
    } finally {
      setSaving(false);
    }
  };

  const handleFontSizeChange = (key, value) => {
    setWidget(prev => ({
      ...prev,
      settings: { ...prev.settings, font: { ...prev.settings.font, [key]: parseInt(value) } }
    }));
  };

  const handleDisplayToggle = (key) => {
    setWidget(prev => ({
      ...prev,
      settings: { ...prev.settings, display: { ...prev.settings.display, [key]: !prev.settings.display[key] } }
    }));
  };

  if (loading) return <div className="widget-loading">ЗАГРУЗКА...</div>;

  const publicFullUrl = widget.public_url ? `${window.location.origin}${widget.public_url}` : 'ВИДЖЕТ НЕ СОЗДАН';

  return (
    <div className="streamer-widget-manager">
      <section className="manager-section access-block">
        <div className="section-header">
          <div className="title-with-icon"><LinkIcon size={18} /><h3>ДОСТУП</h3></div>
          <div className="status-toggle-wrap">
            <span className={`status-badge ${widget.is_active ? 'active' : 'off'}`}>{widget.is_active ? 'ONLINE' : 'OFFLINE'}</span>
            <label className="retro-switch">
              <input type="checkbox" checked={widget.is_active} onChange={(e) => setWidget({...widget, is_active: e.target.checked})} />
              <span className="retro-slider"></span>
            </label>
          </div>
        </div>
        <div className="url-manager">
          <div className={`url-field ${!showUrl ? 'is-blurred' : ''}`}>{publicFullUrl}</div>
          <div className="url-actions-new">
            <button className="square-btn" onClick={() => setShowUrl(!showUrl)}>{showUrl ? <EyeOff size={18} /> : <Eye size={18} />}</button>
            <button className="square-btn" onClick={() => { navigator.clipboard.writeText(publicFullUrl); }}><Copy size={18} /></button>
            <a href={widget.public_url} target="_blank" rel="noopener noreferrer" className="square-btn highlight"><ExternalLink size={18} /></a>
          </div>
        </div>
      </section>

      <div className="manager-grid">
        <section className="manager-section theme-block">
          <div className="section-header"><div className="title-with-icon"><Palette size={18} /><h3>СТИЛИЗАЦИЯ</h3></div></div>
          <div className="pane-content">
            <div className="form-group">
              <label>ПРЕСЕТ ТЕМЫ</label>
              <select className="retro-select-new" value={widget.settings.theme} onChange={(e) => setWidget(prev => {
                const preset = THEME_PRESETS[e.target.value];
                return { ...prev, settings: { ...prev.settings, theme: e.target.value, colors: preset.colors, font: preset.font } };
              })}>
                {Object.keys(THEME_PRESETS).map(k => <option key={k} value={k}>{THEME_PRESETS[k].name}</option>)}
              </select>
            </div>
            <div className="font-size-controls">
              <div className="title-with-icon mini"><Type size={14} /> <label>РАЗМЕРЫ ШРИФТОВ</label></div>
              <div className="slider-group">
                {[
                  ['size_title', 'НИК'], 
                  ['size_meta', 'МЕТА'], 
                  ['size_score', 'СЧЁТ'], 
                  ['size_main', 'ИГРА'], 
                  ['size_timer', 'ТАЙМЕР']
                ].map(([key, label]) => (
                  <div key={key} className="slider-item">
                    <span>{label}: {widget.settings.font[key]}px</span>
                    <input type="range" min="8" max="64" value={widget.settings.font[key]} onChange={(e) => handleFontSizeChange(key, e.target.value)} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="manager-section content-block">
          <div className="section-header"><div className="title-with-icon"><Layout size={18} /><h3>ЭЛЕМЕНТЫ</h3></div></div>
          <div className="pane-content">
            <div className="checkbox-groups">
              {[['show_score', 'ТЕКУЩИЙ СЧЁТ'], ['show_timer', 'ТАЙМЕР СЕССИИ'], ['show_platform', 'ПЛАТФОРМА']].map(([key, label]) => (
                <label key={key} className="retro-check">
                  <input type="checkbox" checked={widget.settings.display[key]} onChange={() => handleDisplayToggle(key)} />
                  <span className="box"></span> <span>{label}</span>
                </label>
              ))}
            </div>
          </div>
        </section>
      </div>

      <section className="manager-section preview-block">
        <div className="section-header"><div className="title-with-icon"><Play size={18} /><h3>ЖИВОЙ ПРЕДПРОСМОТР (300PX)</h3></div></div>
        <div className="preview-canvas">
          <div className={`stream-widget-container vertical theme-${widget.settings.theme}`} 
               style={{ width: '300px', backgroundColor: widget.settings.colors.background, color: widget.settings.colors.text, borderColor: widget.settings.colors.accent, fontFamily: widget.settings.font.family }}>
            <WidgetUI settings={widget.settings} user={currentUser} />
          </div>
        </div>
      </section>

      <div className="manager-footer">
        {message.text && <div className={`retro-toast ${message.type}`}>{message.text}</div>}
        <button className="retro-button-primary save-config-btn" onClick={handleSave} disabled={saving}>
          <Save size={20} /> <span>ЗАФИКСИРОВАТЬ КОНФИГУРАЦИЮ</span>
        </button>
      </div>
    </div>
  );
};

export default StreamerWidgetSettings;
